import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TEntity } from "store/beneficiaryStore";
import type { CustomFieldValue, ICustomField } from "store/customFieldsStore";
import { createCustomField, updateCustomField } from "store/customFieldsStore";
import { useAppDispatch } from "store/store";

import { buildCustomFieldPayload, validateCustomFieldIdentifier } from "../customFieldPayload";

import styles from "./style.module.scss";

interface ICustomFieldForm {
    /**
     * The section on which the action was triggered.
     */
    chosenSection: string;
    /**
     * Type of resource
     */
    resource: "title" | "beneficiary" | "term";
    /**
     * Triggers closing of modal
     */
    setModalType: Dispatch<SetStateAction<string>>;
    /**
     * If form is called on creation or update of field.
     */
    mode: "new" | "edit";
    /**
     * Field being updated (only on edit)
     */
    chosenField?: ICustomField | null;
    /**
     * Triggers update of all fields in parent component.
     */
    onUpdate?: (data: Omit<ICustomField, "id" | "companyId" | "uuid" | "isDeleted">) => void;
    hasPortal: boolean;
}

type ValueTypeOption = { value: CustomFieldValue; label: string };
interface IFormData {
    name: string;
    helperText: string;
    uuid: string;
    valueType: string | CustomFieldValue | ValueTypeOption;
    isRequired: number;
    showOnCreation: number;
    showOnPortal?: number;
    includeInSalesExport?: number;
    entityType: TEntity;
}

export const getValueTypeOptions = (t: (key: string) => string): ValueTypeOption[] => [
    { value: "string", label: t("CustomFields.FieldTypeOptions.String") },
    { value: "date", label: t("CustomFields.FieldTypeOptions.Date") },
    { value: "number", label: t("CustomFields.FieldTypeOptions.Number") },
    { value: "longstring", label: t("CustomFields.FieldTypeOptions.Longstring") },
    { value: "fileimport", label: t("CustomFields.FieldTypeOptions.File") },
    { value: "boolean", label: t("CustomFields.FieldTypeOptions.Boolean") },
];

export const getEntityTypeOptions = (t: (key: string) => string) => [
    { label: t("LegalEntity.AllPhysical"), value: "physical" },
    { label: t("LegalEntity.AllLegal"), value: "legal" },
    { label: t("LegalEntity.All"), value: "none" },
];

// Form used to create or update a custom field withing the relative Settings page.
const CustomFieldForm = ({
    chosenSection,
    resource,
    setModalType,
    mode,
    chosenField,
    onUpdate,
    hasPortal,
}: ICustomFieldForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const valueTypeOptions = useMemo(() => getValueTypeOptions(t), [t]);
    const entityTypeOptions = useMemo(() => getEntityTypeOptions(t), [t]);
    const savedValueType = valueTypeOptions.find((item) => item.value === chosenField?.valueType);
    // const formatBoolean = (value: boolean) => value ? 1 : 0;

    const defaultValues: IFormData = useMemo(() => {
        const entityType = () => {
            if (mode === "edit") {
                // On edit return field's entity type or none.
                return chosenField?.entityType ?? "none";
            } else {
                // On creation, set entityType only if resource type = beneficiary.
                if (resource === "beneficiary") return "physical";
                if (resource === "title") return "none";
                return "none";
            }
        };

        const values = {
            name: mode === "edit" ? chosenField?.name ?? "" : "",
            helperText: mode === "edit" ? chosenField?.helperText ?? "" : "",
            uuid: mode === "edit" ? chosenField?.uuid ?? "" : "",
            showOnCreation: mode === "edit" ? chosenField?.showOnCreation ?? 0 : 0,
            isRequired: mode === "edit" ? chosenField?.isRequired ?? 0 : 0,
            entityType: entityType(),
            valueType:
                mode === "edit"
                    ? savedValueType ?? { value: "string", label: t("CustomFields.FieldTypeOptions.String") }
                    : "",
        };

        if (hasPortal) {
            values["showOnPortal"] = mode === "edit" ? chosenField?.showOnPortal ?? 0 : 0;
        }

        if (resource === "title") {
            values["includeInSalesExport"] = mode === "edit" ? chosenField?.includeInSalesExport ?? 0 : 0;
        }

        return values;
    }, [hasPortal]);

    const showOnCreationOptions = [
        { label: t("CustomFields.CollapseInfo.No"), defaultChecked: true },
        { label: t("CustomFields.CollapseInfo.Yes") },
    ];

    const isRequiredOptions = [
        { label: t("CustomFields.CollapseInfo.No"), defaultChecked: true },
        { label: t("CustomFields.CollapseInfo.Yes") },
    ];

    const showOnPortalOptions = [
        { label: t("CustomFields.CollapseInfo.No"), defaultChecked: true },
        { label: t("CustomFields.CollapseInfo.Yes") },
    ];

    const includeInSalesExportOptions = [
        { label: t("CustomFields.CollapseInfo.No"), defaultChecked: true },
        { label: t("CustomFields.CollapseInfo.Yes") },
    ];

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm({ defaultValues });

    const onSubmit = (data: IFormData) => {
        if (mode === "new") {
            const identifierError = validateCustomFieldIdentifier(data.uuid ?? "");
            if (identifierError) {
                setError("uuid", { message: t(identifierError) });
                return;
            }
        }

        const valueType = (data.valueType as ValueTypeOption).value;
        const req = buildCustomFieldPayload({
            data: { ...data, valueType },
            resource,
            formLocation: chosenSection,
            mode,
        });

        const showDuplicate = (payload: unknown) => {
            const message = (payload as { message?: string } | undefined)?.message ?? "";
            if (message.includes("CustomFieldIdentifierExists")) {
                setError("uuid", { message: t(message) });
            }
        };

        if (mode === "new") {
            dispatch(createCustomField({ data: req })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setModalType("");
                    return;
                }
                showDuplicate(resp.payload);
            });
        }

        if (mode === "edit" && chosenField) {
            const { uuid: _uuid, relatedResource: _relatedResource, ...updateData } = req;
            dispatch(updateCustomField({ fieldId: chosenField?.id, data: updateData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    onUpdate &&
                        onUpdate({
                            ...chosenField,
                            ...updateData,
                            isRequired: +updateData.isRequired,
                            showOnCreation: +updateData.showOnCreation,
                            showOnPortal: +updateData.showOnPortal,
                            includeInSalesExport: +updateData.includeInSalesExport,
                        });
                }
            });
        }
    };

    return (
        <form className={styles["add-field-modal"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                label={t("CustomFields.FormModal.Identifier")}
                control={control}
                controlName="uuid"
                placeholder={mode === "new" ? t("CustomFields.FormModal.IdentifierHelp") : undefined}
                disabled={mode === "edit"}
                error={errors.uuid}
            />

            <TextFormComponent
                label={t("CustomFields.FormModal.Name")}
                control={control}
                controlName="name"
                required
                error={errors.name}
            />

            <TextFormComponent
                label={t("CustomFields.FormModal.Description")}
                control={control}
                controlName="helperText"
                error={errors.helperText}
            />
            <AutocompleteFormComponent
                control={control}
                controlName="valueType"
                options={valueTypeOptions}
                renderOptionText={(option) => option.label ?? ""}
                label={t("CustomFields.FormModal.FieldType")}
                placeholder={t("CustomFields.FormModal.FieldTypePlaceholder")}
                isOptionEqualToValue={(option, value) => option?.label === value?.label}
                required
                error={errors.valueType}
            />
            {resource === "beneficiary" && (
                <SelectFormComponent
                    control={control}
                    controlName="entityType"
                    items={entityTypeOptions}
                    label={t("CustomFields.FormModal.Entity")}
                />
            )}

            <RadioFormComponent
                control={control}
                controlName="showOnCreation"
                label={t("CustomFields.FormModal.ShowOnCreation")}
                options={showOnCreationOptions}
            />

            <RadioFormComponent
                control={control}
                controlName="isRequired"
                label={t("CustomFields.FormModal.MandatoryField")}
                options={isRequiredOptions}
            />
            {resource !== "term" && (
                <RadioFormComponent
                    control={control}
                    controlName="showOnPortal"
                    label={t("CustomFields.FormModal.ShowOnPortal")}
                    options={showOnPortalOptions}
                />
            )}
            {resource === "title" && (
                <RadioFormComponent
                    control={control}
                    controlName="includeInSalesExport"
                    label={t("CustomFields.FormModal.IncludeInSalesExport")}
                    options={includeInSalesExportOptions}
                />
            )}
            <StyledButton buttonType="primary" fullWidth type="submit">
                {mode === "new" ? t("CustomFields.FormModal.AddField") : t("CustomFields.FormModal.ConfirmChanges")}
            </StyledButton>
        </form>
    );
};

export default CustomFieldForm;
